import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, getDoc, addDoc, Timestamp, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSort, faTimes, faCheck, faXmark, faShield } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import ContentReviewModal from '../components/admin/ContentReviewModal';
import { PageHeader, Button } from '../design-system';

const TABS = [
  { id: 'pending-content', label: 'Pending Content', count: 0 },
  { id: 'pending-tips', label: 'Pending Tips', count: 0 },
  { id: 'pending-comments', label: 'Pending Comments', count: 0 },
  { id: 'reported-content', label: 'Reported Content', count: 0 },
  { id: 'all-content', label: 'All Content', count: 0 },
];

const CONTENT_TYPES = ['All', 'Recipe', 'Activity', 'Story'];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
];

export default function Admin() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending-content');
  const [submissions, setSubmissions] = useState([]);
  const [comments, setComments] = useState([]);
  const [allSubmissions, setAllSubmissions] = useState([]); // For statistics
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedSubmissions, setSelectedSubmissions] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [reportedComments, setReportedComments] = useState([]);
  const [reportedContent, setReportedContent] = useState([]);

  // Load all submissions for statistics
  useEffect(() => {
    if (!user) return;

    const statsQ = query(
      collection(db, 'submissions'),
      orderBy('submittedAt', 'desc')
    );

    const unsubscribeStats = onSnapshot(
      statsQ,
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAllSubmissions(data);
      },
      (error) => {
        console.error('Error loading stats:', error);
      }
    );

    return () => unsubscribeStats();
  }, [user]);

  // Clear selection when tab changes
  useEffect(() => {
    setSelectedSubmissions(new Set());
  }, [activeTab]);

  // Load pending submissions
  useEffect(() => {
    if (!user) return;

    let unsubscribe = null;

    const loadSubmissions = () => {
      let q;
      
      if (activeTab === 'pending-content') {
        q = query(
          collection(db, 'submissions'),
          where('status', '==', 'pending'),
          orderBy('submittedAt', 'desc')
        );
      } else if (activeTab === 'all-content') {
        q = query(
          collection(db, 'submissions'),
          orderBy('submittedAt', 'desc'),
          limit(100) // Limit to prevent loading too many at once
        );
      } else if (activeTab === 'pending-comments') {
        // Load pending comments
        const commentsQ = query(
          collection(db, 'comments'),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );
        unsubscribe = onSnapshot(
          commentsQ,
          (snapshot) => {
            const data = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setComments(data);
            setSubmissions([]);
            setLoading(false);
          },
          (error) => {
            console.error('Error loading comments:', error);
            setLoading(false);
          }
        );
        return;
      } else if (activeTab === 'reported-content') {
        // Load reported comments and content
        const reportedCommentsQuery = query(
          collection(db, 'comments'),
          where('reportedBy', '!=', []),
          orderBy('reportedBy'),
          orderBy('createdAt', 'desc')
        );
        
        const unsubscribeComments = onSnapshot(
          reportedCommentsQuery,
          (snapshot) => {
            const reported = snapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .filter(comment => (comment.reportedBy || []).length > 0);
            setReportedComments(reported);
          },
          (error) => {
            // Try without orderBy if index doesn't exist
            const simpleQuery = query(
              collection(db, 'comments'),
              orderBy('createdAt', 'desc')
            );
            const unsubscribeSimple = onSnapshot(
              simpleQuery,
              (snapshot) => {
                const reported = snapshot.docs
                  .map(doc => ({ id: doc.id, ...doc.data() }))
                  .filter(comment => (comment.reportedBy || []).length > 0);
                setReportedComments(reported);
              },
              (error) => {
                console.error('Error loading reported comments:', error);
                setReportedComments([]);
              }
            );
            return () => unsubscribeSimple();
          }
        );
        
        // Load reported content (from content collection)
        const contentQuery = query(
          collection(db, 'content'),
          orderBy('createdAt', 'desc')
        );
        
        const unsubscribeContent = onSnapshot(
          contentQuery,
          (snapshot) => {
            const reported = snapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .filter(content => (content.reportedBy || []).length > 0);
            setReportedContent(reported);
          },
          (error) => {
            console.error('Error loading reported content:', error);
            setReportedContent([]);
          }
        );
        
        setLoading(false);
        return () => {
          unsubscribeComments();
          unsubscribeContent();
        };
      } else {
        // For pending-tips, we'll implement later
        setSubmissions([]);
        setComments([]);
        setLoading(false);
        return;
      }

      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const data = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Sort by submittedAt in case index isn't ready yet
          if (activeTab === 'pending-content') {
            data.sort((a, b) => {
              const aTime = a.submittedAt?.toDate ? a.submittedAt.toDate().getTime() : (a.submittedAt ? new Date(a.submittedAt).getTime() : 0);
              const bTime = b.submittedAt?.toDate ? b.submittedAt.toDate().getTime() : (b.submittedAt ? new Date(b.submittedAt).getTime() : 0);
              return bTime - aTime; // Descending order
            });
          }
          setSubmissions(data);
          setLoading(false);
        },
        (error) => {
          console.error('Error loading submissions:', error);
          // If index error, try without orderBy as fallback
          if (error.code === 'failed-precondition' && error.message?.includes('index')) {
            console.log('Index not ready yet, loading without orderBy...');
            let fallbackQuery;
            if (activeTab === 'pending-content') {
              fallbackQuery = query(
                collection(db, 'submissions'),
                where('status', '==', 'pending')
              );
            } else if (activeTab === 'all-content') {
              fallbackQuery = query(
                collection(db, 'submissions'),
                limit(100)
              );
            } else {
              toast.error('Failed to load submissions');
              setLoading(false);
              return;
            }
            
            const fallbackUnsubscribe = onSnapshot(fallbackQuery, (snapshot) => {
              const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
              // Sort by submittedAt in memory
              data.sort((a, b) => {
                const aTime = a.submittedAt?.toDate ? a.submittedAt.toDate().getTime() : (a.submittedAt ? new Date(a.submittedAt).getTime() : 0);
                const bTime = b.submittedAt?.toDate ? b.submittedAt.toDate().getTime() : (b.submittedAt ? new Date(b.submittedAt).getTime() : 0);
                return bTime - aTime; // Descending order
              });
              setSubmissions(data);
              setLoading(false);
            }, (fallbackError) => {
              console.error('Error loading submissions (fallback):', fallbackError);
              toast.error('Failed to load submissions');
              setLoading(false);
            });
            unsubscribe = fallbackUnsubscribe;
          } else {
            toast.error('Failed to load submissions');
            setLoading(false);
          }
        }
      );
    };

    loadSubmissions();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user, activeTab]);

  // Filter and sort submissions
  const filteredSubmissions = submissions
    .filter(sub => {
      if (typeFilter === 'All') return true;
      return sub.type === typeFilter.toLowerCase();
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return b.submittedAt?.toMillis() - a.submittedAt?.toMillis();
      } else {
        return a.submittedAt?.toMillis() - b.submittedAt?.toMillis();
      }
    });

  const handleReview = (submission) => {
    setSelectedSubmission(submission);
    setShowReviewModal(true);
  };

  const handleApprove = async (submissionId, editedContent = null) => {
    try {
      const submissionRef = doc(db, 'submissions', submissionId);
      const submissionDoc = await getDoc(submissionRef);
      
      if (!submissionDoc.exists()) {
        toast.error('Submission not found');
        return;
      }

      const submissionData = submissionDoc.data();
      const contentToApprove = editedContent || submissionData.content;

      // Move to /content collection
      const contentDocRef = await addDoc(collection(db, 'content'), {
        ...submissionData,
        content: contentToApprove,
        status: 'approved',
        reviewedAt: Timestamp.now(),
        reviewedBy: user.uid,
        updatedAt: Timestamp.now()
      });

      // Update submission status with contentId for linking
      await updateDoc(submissionRef, {
        status: 'approved',
        contentId: contentDocRef.id, // Store the content document ID
        reviewedAt: Timestamp.now(),
        reviewedBy: user.uid,
        updatedAt: Timestamp.now()
      });

      toast.success('Content approved and published!');
      setShowReviewModal(false);
      setSelectedSubmission(null);
    } catch (error) {
      console.error('Error approving submission:', error);
      toast.error('Failed to approve submission');
    }
  };

  const handleReject = async (submissionId, rejectionReason) => {
    try {
      const submissionRef = doc(db, 'submissions', submissionId);
      await updateDoc(submissionRef, {
        status: 'rejected',
        rejectionReason: rejectionReason,
        reviewedAt: Timestamp.now(),
        reviewedBy: user.uid,
        updatedAt: Timestamp.now()
      });

      toast.success('Submission rejected');
      setShowReviewModal(false);
      setSelectedSubmission(null);
    } catch (error) {
      console.error('Error rejecting submission:', error);
      toast.error('Failed to reject submission');
    }
  };

  const handleCommentApprove = async (commentId) => {
    try {
      const commentRef = doc(db, 'comments', commentId);
      await updateDoc(commentRef, {
        status: 'approved',
        approvedAt: Timestamp.now(),
        approvedBy: user.uid
      });

      toast.success('Comment approved!');
      setShowCommentModal(false);
      setSelectedComment(null);
    } catch (error) {
      console.error('Error approving comment:', error);
      toast.error('Failed to approve comment');
    }
  };

  const handleCommentReject = async (commentId, rejectionReason) => {
    try {
      const commentRef = doc(db, 'comments', commentId);
      await updateDoc(commentRef, {
        status: 'rejected',
        rejectionReason: rejectionReason,
        reviewedAt: Timestamp.now(),
        reviewedBy: user.uid
      });

      toast.success('Comment rejected');
      setShowCommentModal(false);
      setSelectedComment(null);
    } catch (error) {
      console.error('Error rejecting comment:', error);
      toast.error('Failed to reject comment');
    }
  };

  const handleCommentReview = (comment) => {
    setSelectedComment(comment);
    setShowCommentModal(true);
  };

  // Bulk selection handlers
  const handleSelectSubmission = (submissionId) => {
    setSelectedSubmissions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(submissionId)) {
        newSet.delete(submissionId);
      } else {
        newSet.add(submissionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedSubmissions.size === filteredSubmissions.length) {
      setSelectedSubmissions(new Set());
    } else {
      setSelectedSubmissions(new Set(filteredSubmissions.map(s => s.id)));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedSubmissions.size === 0) {
      toast.error('Please select at least one submission');
      return;
    }

    const confirm = window.confirm(`Approve ${selectedSubmissions.size} submission(s)?`);
    if (!confirm) return;

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const submissionId of selectedSubmissions) {
        try {
          await handleApprove(submissionId);
          successCount++;
        } catch (error) {
          console.error(`Error approving ${submissionId}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully approved ${successCount} submission(s)`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to approve ${errorCount} submission(s)`);
      }

      setSelectedSubmissions(new Set());
    } catch (error) {
      console.error('Error in bulk approve:', error);
      toast.error('Error during bulk approval');
    }
  };

  const handleBulkReject = async () => {
    if (selectedSubmissions.size === 0) {
      toast.error('Please select at least one submission');
      return;
    }

    const rejectionReason = window.prompt(
      `Reject ${selectedSubmissions.size} submission(s)?\n\nPlease provide a reason (this will be applied to all selected):`
    );
    
    if (!rejectionReason || !rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const submissionId of selectedSubmissions) {
        try {
          await handleReject(submissionId, rejectionReason.trim());
          successCount++;
        } catch (error) {
          console.error(`Error rejecting ${submissionId}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully rejected ${successCount} submission(s)`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to reject ${errorCount} submission(s)`);
      }

      setSelectedSubmissions(new Set());
    } catch (error) {
      console.error('Error in bulk reject:', error);
      toast.error('Error during bulk rejection');
    }
  };

  // Update showBulkActions when selection changes
  useEffect(() => {
    setShowBulkActions(selectedSubmissions.size > 0);
  }, [selectedSubmissions.size]);

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'recipe': return 'bg-primary/20 text-primary-dark';
      case 'activity': return 'bg-blue-100 text-blue-800';
      case 'story': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate statistics
  const stats = {
    totalPending: allSubmissions.filter(s => s.status === 'pending').length,
    totalApproved: allSubmissions.filter(s => s.status === 'approved').length,
    totalRejected: allSubmissions.filter(s => s.status === 'rejected').length,
    approvedToday: allSubmissions.filter(s => {
      if (s.status !== 'approved' || !s.reviewedAt) return false;
      const reviewedDate = s.reviewedAt.toDate();
      const today = new Date();
      return reviewedDate.toDateString() === today.toDateString();
    }).length,
    pendingComments: comments.length,
    reportedItems: reportedComments.length + reportedContent.length,
    rejectionRate: allSubmissions.length > 0
      ? Math.round((allSubmissions.filter(s => s.status === 'rejected').length / allSubmissions.length) * 100)
      : 0,
  };

  return (
    <div className="min-h-screen bg-background pt-14 pb-20 md:pt-0 md:pb-0">
      <PageHeader
        title="Admin Dashboard"
        subtitle="Review and moderate community content submissions"
        actions={
          activeTab === 'pending-comments' && (
              <Button
                onClick={async () => {
                  if (!window.confirm('Create 5 sample pending comments for testing?')) return;
                  
                  const sampleComments = [
                    {
                      contentId: 'challah',
                      contentType: 'recipe',
                      userId: 'sample_user_1',
                      userName: 'Sarah',
                      familyContext: 'Family of 2 kids (ages 5, 8)',
                      text: 'This recipe worked perfectly! My kids loved helping with the braiding. We made it for Shabbat dinner last week.',
                      status: 'pending',
                      hearts: 0,
                      heartedBy: [],
                      reportedBy: [],
                      createdAt: Timestamp.now(),
                    },
                    {
                      contentId: 'challah',
                      contentType: 'recipe',
                      userId: 'sample_user_2',
                      userName: 'Michael',
                      familyContext: 'Family',
                      text: 'Has anyone tried adding raisins? Thinking about making it sweeter for the kids.',
                      status: 'pending',
                      hearts: 0,
                      heartedBy: [],
                      reportedBy: [],
                      createdAt: Timestamp.fromDate(new Date(Date.now() - 2 * 60 * 60 * 1000)), // 2 hours ago
                    },
                    {
                      contentId: 'passover-seder',
                      contentType: 'activity',
                      userId: 'sample_user_3',
                      userName: 'Rachel',
                      familyContext: 'Family of 3 kids (ages 4, 7, 10)',
                      text: 'We did this last year and it was a hit! The kids especially loved the afikoman hunt. Highly recommend for families with young children.',
                      status: 'pending',
                      hearts: 0,
                      heartedBy: [],
                      reportedBy: [],
                      createdAt: Timestamp.fromDate(new Date(Date.now() - 5 * 60 * 60 * 1000)), // 5 hours ago
                    },
                    {
                      contentId: 'story_1',
                      contentType: 'story',
                      userId: 'sample_user_4',
                      userName: 'David',
                      familyContext: 'Family of 1 kid (age 6)',
                      text: 'Great story! My daughter asked to read it again right away. The illustrations are beautiful too.',
                      status: 'pending',
                      hearts: 0,
                      heartedBy: [],
                      reportedBy: [],
                      createdAt: Timestamp.fromDate(new Date(Date.now() - 24 * 60 * 60 * 1000)), // 1 day ago
                    },
                    {
                      contentId: 'story_1',
                      contentType: 'story',
                      userId: 'sample_user_5',
                      userName: 'Emma',
                      familyContext: 'Family of 2 kids (ages 3, 9)',
                      text: 'Perfect for bedtime reading. The message about kindness really resonated with my older one.',
                      status: 'pending',
                      hearts: 0,
                      heartedBy: [],
                      reportedBy: [],
                      createdAt: Timestamp.fromDate(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)), // 3 days ago
                    },
                  ];
                  
                  try {
                    for (const comment of sampleComments) {
                      await addDoc(collection(db, 'comments'), comment);
                    }
                    toast.success('Created 5 sample comments');
                  } catch (error) {
                    console.error('Error creating sample comments:', error);
                    toast.error('Failed to create sample comments');
                  }
                }}
                className="text-sm"
              >
                + Add Sample Comments
              </Button>
          )
        }
      />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-8 md:py-10">
        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-border p-4 shadow-sm">
            <div className="text-2xl font-bold text-yellow-600">{stats.totalPending}</div>
            <div className="text-xs text-text-secondary mt-1">Pending</div>
          </div>
          <div className="bg-white rounded-lg border border-border p-4 shadow-sm">
            <div className="text-2xl font-bold text-green-600">{stats.totalApproved}</div>
            <div className="text-xs text-text-secondary mt-1">Approved</div>
          </div>
          <div className="bg-white rounded-lg border border-border p-4 shadow-sm">
            <div className="text-2xl font-bold text-red-600">{stats.totalRejected}</div>
            <div className="text-xs text-text-secondary mt-1">Rejected</div>
          </div>
          <div className="bg-white rounded-lg border border-border p-4 shadow-sm">
            <div className="text-2xl font-bold text-primary">{stats.approvedToday}</div>
            <div className="text-xs text-text-secondary mt-1">Approved Today</div>
          </div>
          <div className="bg-white rounded-lg border border-border p-4 shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{stats.pendingComments}</div>
            <div className="text-xs text-text-secondary mt-1">Pending Comments</div>
          </div>
          <div className="bg-white rounded-lg border border-border p-4 shadow-sm">
            <div className="text-2xl font-bold text-primary">{stats.reportedItems}</div>
            <div className="text-xs text-text-secondary mt-1">Reported Items</div>
          </div>
          <div className="bg-white rounded-lg border border-border p-4 shadow-sm">
            <div className="text-2xl font-bold text-text-primary">{stats.rejectionRate}%</div>
            <div className="text-xs text-text-secondary mt-1">Rejection Rate</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 sm:gap-3 mb-6 border-b border-border">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-semibold border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-primary text-white text-xs rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Filters and Sort */}
        {activeTab === 'pending-content' && (
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faFilter} className="text-text-secondary" />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {CONTENT_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faSort} className="text-text-secondary" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {SORT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="text-sm text-text-secondary">
              {filteredSubmissions.length} {filteredSubmissions.length === 1 ? 'submission' : 'submissions'}
            </div>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {showBulkActions && activeTab === 'pending-content' && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-semibold text-text-primary">
                {selectedSubmissions.size} selected
              </span>
              <button
                onClick={handleSelectAll}
                className="text-sm text-primary hover:underline"
              >
                {selectedSubmissions.size === filteredSubmissions.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleBulkApprove}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold"
              >
                <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
                Approve Selected
              </button>
              <button
                onClick={handleBulkReject}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold"
              >
                <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                Reject Selected
              </button>
              <button
                onClick={() => setSelectedSubmissions(new Set())}
                className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors text-sm font-semibold"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Content List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-text-secondary">Loading...</div>
          </div>
        ) : activeTab === 'pending-comments' ? (
          comments.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-text-secondary">No pending comments found</div>
            </div>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  className="bg-white rounded-card shadow-card border border-border p-6 hover:shadow-card-hover transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-text-primary">{comment.userName}</span>
                        {comment.familyContext && (
                          <span className="text-xs text-text-secondary bg-surface px-2 py-1 rounded-full">
                            {comment.familyContext}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-text-secondary mb-2">
                        On {comment.contentType} • {comment.createdAt && formatDistanceToNow(comment.createdAt.toDate(), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                  <p className="text-text-primary mb-4 leading-relaxed">{comment.text}</p>
                  <div className="flex items-center justify-end">
                    <button
                      onClick={() => handleCommentReview(comment)}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-semibold"
                    >
                      Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : activeTab === 'reported-content' ? (
          <div className="text-center py-12">
            <div className="text-text-secondary mb-4">Reported Content</div>
            <p className="text-sm text-text-secondary">
              Content reporting feature coming soon. Users will be able to report inappropriate content for review.
            </p>
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-text-secondary">No {activeTab === 'pending-content' ? 'pending' : ''} submissions found</div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSubmissions.map((submission) => (
              <div
                key={submission.id}
                className="bg-white rounded-card shadow-card border border-border p-6 hover:shadow-card-hover transition-all"
              >
                {/* Checkbox for bulk selection */}
                {activeTab === 'pending-content' ? (
                  <div className="flex items-start gap-4">
                    <input
                      type="checkbox"
                      checked={selectedSubmissions.has(submission.id)}
                      onChange={() => handleSelectSubmission(submission.id)}
                      className="mt-1 w-5 h-5 text-primary border-border rounded focus:ring-primary"
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeBadgeColor(submission.type)}`}>
                            {submission.type.charAt(0).toUpperCase() + submission.type.slice(1)}
                          </span>
                        </div>
                        <div className="text-sm text-text-secondary">
                          {submission.submittedAt && formatDistanceToNow(submission.submittedAt.toDate(), { addSuffix: true })}
                        </div>
                      </div>

                      <h3 className="text-lg font-semibold text-text-primary mb-2">
                        {submission.content?.title || 'Untitled'}
                      </h3>
                      <p className="text-sm text-text-secondary mb-4 line-clamp-2">
                        {submission.content?.description || 'No description'}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="text-sm text-text-secondary">
                          Submitted by <span className="font-semibold">{submission.submitterName}</span>
                        </div>
                        {submission.status === 'pending' && (
                          <button
                            onClick={() => handleReview(submission)}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-semibold"
                          >
                            Review
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeBadgeColor(submission.type)}`}>
                          {submission.type.charAt(0).toUpperCase() + submission.type.slice(1)}
                        </span>
                        {activeTab === 'all-content' && (
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(submission.status)}`}>
                            {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-text-secondary">
                        {submission.submittedAt && formatDistanceToNow(submission.submittedAt.toDate(), { addSuffix: true })}
                      </div>
                    </div>

                    <h3 className="text-lg font-semibold text-text-primary mb-2">
                      {submission.content?.title || 'Untitled'}
                    </h3>
                    <p className="text-sm text-text-secondary mb-4 line-clamp-2">
                      {submission.content?.description || 'No description'}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-text-secondary">
                        Submitted by <span className="font-semibold">{submission.submitterName}</span>
                      </div>
                      {submission.status === 'pending' && (
                        <button
                          onClick={() => handleReview(submission)}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-semibold"
                        >
                          Review
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Review Modal */}
        {showReviewModal && selectedSubmission && (
          <ContentReviewModal
            submission={selectedSubmission}
            isOpen={showReviewModal}
            onClose={() => {
              setShowReviewModal(false);
              setSelectedSubmission(null);
            }}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        )}

        {/* Comment Review Modal */}
        {showCommentModal && selectedComment && (
          <CommentReviewModal
            comment={selectedComment}
            isOpen={showCommentModal}
            onClose={() => {
              setShowCommentModal(false);
              setSelectedComment(null);
            }}
            onApprove={handleCommentApprove}
            onReject={handleCommentReject}
          />
        )}
      </div>
    </div>
  );
}

// Comment Review Modal Component
function CommentReviewModal({ comment, isOpen, onClose, onApprove, onReject }) {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowRejectForm(false);
      setRejectionReason('');
    }
  }, [isOpen]);

  if (!isOpen || !comment) return null;

  const handleRejectClick = () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    onReject(comment.id, rejectionReason);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-semibold text-text-primary">Review Comment</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <FontAwesomeIcon icon={faTimes} className="w-5 h-5 text-text-secondary" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <div>
              <p className="text-sm text-text-secondary mb-1">Commenter</p>
              <p className="font-semibold text-text-primary">{comment.userName}</p>
              {comment.familyContext && (
                <p className="text-sm text-text-secondary">{comment.familyContext}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-text-secondary mb-1">Content</p>
              <p className="text-text-primary">{comment.contentType} • ID: {comment.contentId}</p>
            </div>
            <div>
              <p className="text-sm text-text-secondary mb-1">Comment</p>
              <p className="text-text-primary leading-relaxed">{comment.text}</p>
            </div>
            <div>
              <p className="text-sm text-text-secondary mb-1">Submitted</p>
              <p className="text-text-primary">
                {comment.createdAt && formatDistanceToNow(comment.createdAt.toDate(), { addSuffix: true })}
              </p>
            </div>
            {showRejectForm && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-red-800 mb-2">Rejection Reason</p>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  placeholder="Please provide a reason for rejection..."
                  className="w-full px-4 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between p-6 border-t border-border">
          <button onClick={onClose} className="px-6 py-2 text-text-secondary hover:text-text-primary">
            Cancel
          </button>
          <div className="flex gap-3">
            {!showRejectForm ? (
              <>
                <button
                  onClick={() => setShowRejectForm(true)}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Reject
                </button>
                <button
                  onClick={() => onApprove(comment.id)}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  Approve
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setShowRejectForm(false);
                    setRejectionReason('');
                  }}
                  className="px-6 py-2 text-text-secondary hover:text-text-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectClick}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Confirm Rejection
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

